import {
  adminData,
  supervisorData,
  userData,
  type NavItem,
  type NavSubItem,
  type NavSubMenu,
} from "@/data/navData";

export interface Crumb {
  label: string;
  href?: string;
}

type NavNode = NavItem | NavSubItem | NavSubMenu;

/** The sidebar tree for the shell `pathname` belongs to -- the same data
 * each sidebar renders, so a breadcrumb can never disagree with it. */
function navFor(pathname: string): NavNode[] {
  if (pathname.startsWith("/admin")) return adminData.mainMenuItems;
  if (pathname.startsWith("/supervisor")) return supervisorData.mainMenuItems;
  return userData.mainMenuItems;
}

function isUnder(pathname: string, url: string): boolean {
  return pathname === url || pathname.startsWith(`${url}/`);
}

interface Match {
  ancestors: NavNode[];
  leaf: NavNode;
}

/** The sidebar leaf whose url is the longest match for `pathname` (so
 * /admin/inventory/items/12 lands on Items, not the Inventory dashboard at
 * /admin/inventory), with the groups above it. Only leaves are pages: a
 * group's own url just points at its first child. */
function findLeaf(nodes: NavNode[], pathname: string): Match | null {
  const leaves: Match[] = [];
  const walk = (list: NavNode[], ancestors: NavNode[]) => {
    for (const node of list) {
      const children = "items" in node ? node.items : undefined;
      if (children && children.length > 0) {
        walk(children, [...ancestors, node]);
      } else if (node.url !== "#") {
        leaves.push({ ancestors, leaf: node });
      }
    }
  };
  walk(nodes, []);

  // A leaf that other leaves live under (/admin, /admin/inventory -- the
  // dashboards) matches only itself. Otherwise every page missing from the
  // sidebar (/admin/profile-management) would read as "Dashboard".
  const isSectionRoot = (url: string) =>
    leaves.some(({ leaf }) => leaf.url.startsWith(`${url}/`));

  let best: Match | null = null;
  for (const match of leaves) {
    const { url } = match.leaf;
    const matches = isSectionRoot(url)
      ? pathname === url
      : isUnder(pathname, url);
    if (matches && (!best || url.length > best.leaf.url.length)) best = match;
  }
  return best;
}

/**
 * Breadcrumbs for `pathname`, derived from the sidebar: its groups as plain
 * text (they aren't pages), then the page. A detail page passes `current`
 * (the record's own label, e.g. "IRA-00003"), which follows the list page,
 * now a link back to it.
 *
 * A group whose only visible difference from its leaf is the same title
 * (Notifications > Notifications) is shown once.
 *
 * Returns null when the page isn't in the sidebar at all -- the caller then
 * falls back to breadcrumbs the page supplies itself.
 */
export function deriveBreadcrumbs(
  pathname: string,
  current?: string,
): Crumb[] | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const match = findLeaf(navFor(path), path);
  if (!match) return null;

  const { ancestors, leaf } = match;
  const groups = ancestors.filter(
    (group, index) =>
      group.title !== (ancestors[index + 1] ?? leaf).title,
  );
  const crumbs: Crumb[] = groups.map((group) => ({ label: group.title }));
  const onListPage = path === leaf.url;
  crumbs.push({ label: leaf.title, href: onListPage ? undefined : leaf.url });
  if (current && !onListPage) crumbs.push({ label: current });
  return crumbs;
}
